import { SIGNALING_URL } from "../config";

/** Messages exchanged through the signaling relay (verbatim, opaque to it). */
export type SignalMessage =
  | { type: "welcome"; initiator: boolean; peers: number }
  | { type: "peers"; peers: number }
  | { type: "peer-left" }
  | { type: "full" }
  | { type: "offer"; sdp: string }
  | { type: "answer"; sdp: string }
  | { type: "ice"; candidate: RTCIceCandidateInit };

export interface SignalingHandlers {
  onMessage: (msg: SignalMessage) => void;
  onClose: () => void;
  onError: (err: Event) => void;
}

/**
 * Thin WebSocket client to the signaling Worker. Connects to `/room/<roomId>`
 * and exchanges JSON control messages. Carries SDP/ICE only — never the token,
 * psk, or any file data.
 */
export class SignalingClient {
  private ws?: WebSocket;

  constructor(private readonly handlers: SignalingHandlers) {}

  connect(roomId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(`${SIGNALING_URL}/room/${roomId}`);
      this.ws = ws;

      ws.onopen = () => resolve();
      ws.onerror = (e) => {
        this.handlers.onError(e);
        reject(new Error("signaling connection failed"));
      };
      ws.onclose = () => this.handlers.onClose();
      ws.onmessage = (event) => {
        try {
          this.handlers.onMessage(
            JSON.parse(event.data as string) as SignalMessage
          );
        } catch {
          // ignore malformed control frames
        }
      };
    });
  }

  send(msg: SignalMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  close(): void {
    try {
      this.ws?.close();
    } catch {
      // already closed
    }
    this.ws = undefined;
  }
}
